export const POINTS_LIFECYCLE_CRITERIA = [
  {
    id: "no_login",
    label: "No Login Activity",
  },
  {
    id: "no_points_earned",
    label: "No Points Earned",
  },
  {
    id: "no_activity_participation",
    label: "No Activity Participation",
  },
  {
    id: "no_points_redeemed",
    label: "No Points Redeemed",
  },
];

export const CRITERIA_OPERATORS = ["AND", "OR"];

const ALLOWED_CRITERIA = new Set(
  POINTS_LIFECYCLE_CRITERIA.map((criterion) => criterion.id)
);

const hasValue = (value) => value !== undefined && value !== null && value !== "";
const hasCriteria = (criteria) => Array.isArray(criteria) && criteria.length > 0;

const toNumberOrUndefined = (value) => {
  if (!hasValue(value)) {
    return undefined;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

export const filterCriteria = (criteria = []) =>
  Array.isArray(criteria)
    ? criteria.filter((criterion) => ALLOWED_CRITERIA.has(criterion))
    : [];

export const normalizePointsExpiration = (pointsExpiration = {}) => {
  const windowBased = pointsExpiration?.rules?.window_based || {};
  const inactivityBased = pointsExpiration?.rules?.inactivity_based || {};
  const pendingPointsVoid = pointsExpiration?.rules?.pending_points_void || {};
  const reservationCancellation =
    pointsExpiration?.rules?.reservation_cancellation || {};
  const isWindowBasedEnabled =
    windowBased.enabled ?? hasValue(windowBased.expiration_period_days);
  const isInactivityBasedEnabled =
    inactivityBased.enabled ??
    (hasValue(inactivityBased.expiration_period_days) ||
      hasCriteria(inactivityBased.criteria));
  const shouldEnableWindowBased = !!isWindowBasedEnabled;
  const shouldEnableInactivityBased = !!isInactivityBasedEnabled;

  const normalizedWindowBased = {
    enabled: shouldEnableWindowBased,
  };
  const windowExpirationPeriod = toNumberOrUndefined(
    windowBased.expiration_period_days
  );
  if (normalizedWindowBased.enabled && windowExpirationPeriod !== undefined) {
    normalizedWindowBased.expiration_period_days = windowExpirationPeriod;
  }

  const normalizedInactivityBased = {
    enabled: shouldEnableInactivityBased,
  };
  const inactivityExpirationPeriod = toNumberOrUndefined(
    inactivityBased.expiration_period_days
  );
  if (normalizedInactivityBased.enabled) {
    if (inactivityExpirationPeriod !== undefined) {
      normalizedInactivityBased.expiration_period_days = inactivityExpirationPeriod;
    }

    normalizedInactivityBased.criteria_operator = CRITERIA_OPERATORS.includes(
      inactivityBased.criteria_operator
    )
      ? inactivityBased.criteria_operator
      : "OR";
    normalizedInactivityBased.criteria = filterCriteria(inactivityBased.criteria);
  }

  const normalizedPendingPointsVoid = {};
  const pendingPointsVoidReleaseDays = toNumberOrUndefined(
    pendingPointsVoid.release_after_days
  );
  if (pendingPointsVoidReleaseDays !== undefined) {
    normalizedPendingPointsVoid.release_after_days = pendingPointsVoidReleaseDays;
  }

  const normalizedReservationCancellation = {};
  const reservationCancellationReleaseDays = toNumberOrUndefined(
    reservationCancellation.release_after_days
  );
  if (reservationCancellationReleaseDays !== undefined) {
    normalizedReservationCancellation.release_after_days =
      reservationCancellationReleaseDays;
  }

  return {
    ...pointsExpiration,
    enabled: true,
    rules: {
      ...(pointsExpiration?.rules || {}),
      window_based: normalizedWindowBased,
      inactivity_based: normalizedInactivityBased,
      pending_points_void: normalizedPendingPointsVoid,
      reservation_cancellation: normalizedReservationCancellation,
    },
  };
};

const isValidValue = (value, min = 0) => hasValue(value) && Number(value) >= min;

export const isPointsLifecycleValid = (pointsExpiration = {}) => {
  const rules = pointsExpiration?.rules || {};
  const windowBased = rules.window_based || {};
  const inactivityBased = rules.inactivity_based || {};
  const pendingPointsVoid = rules.pending_points_void || {};
  const reservationCancellation = rules.reservation_cancellation || {};

  if (windowBased.enabled && !isValidValue(windowBased.expiration_period_days, 1)) {
    return false;
  }

  if (
    inactivityBased.enabled &&
    !isValidValue(inactivityBased.expiration_period_days, 1)
  ) {
    return false;
  }

  if (!isValidValue(pendingPointsVoid.release_after_days)) {
    return false;
  }

  if (!isValidValue(reservationCancellation.release_after_days)) {
    return false;
  }

  return true;
};

export const normalizeGlobalPointsConfiguration = (globalPointsConfig = {}) => ({
  ...globalPointsConfig,
  points_expiration: normalizePointsExpiration(
    globalPointsConfig?.points_expiration
  ),
});
